import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Divider, Form, Modal, Row, Space, Spin } from "antd";
import { makeStyles } from "@material-ui/core/styles";
import CustomButton from "components/Common/buttons/CustomButton";
import NGISelect from "components/Common/inputs/NGISelect";
import { Option } from "antd/es/mentions";
import NGIFormItem from "components/Common/form/NGIFormItem";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { uniq } from "lodash";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import NGICheckbox from "components/Common/inputs/NGICheckbox";
import NGITextArea from "components/Common/inputs/NGITextArea";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import { DICTIONARY_TYPES } from "store/dictionaries/constants";
import { createDictionary } from "utils/api";
import classnames from "classnames";

const useStyles = makeStyles({
  modal: {
    maxWidth: "900px!important",
  },
  infoContainer: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span:first-of-type": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  fieldTitle: {
    fontSize: 15,
    fontWeight: 600,
  },
  subTitle: {
    fontSize: 13,
    fontWeight: 300,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    display: "flex",

    "& .ant-col + .ant-col": {
      paddingLeft: 10,
    },
    "& .ant-form-item": {
      marginBottom: 0,
    },
  },
  marginTop: {
    marginTop: 15,
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.6,
  },
  select: {
    width: "100%",

    "&.ant-select:not(.ant-select-customize-input) .ant-select-selector": {
      borderRadius: 5,
      border: "1px solid #0000001A",
    },
    "&.ant-select-disabled:not(.ant-select-borderless)": {
      "&>.ant-select-selector": {
        border: "1px solid #d9d9d9!important",
      },
    },
  },
  warnContainer: {
    backgroundColor: "#DB660714",
    padding: 15,
    margin: "20px 0",
    borderRadius: 5,
  },
  textarea: {
    border: "1px solid #0000001A!important",
    marginTop: 10,
  },
  error: {
    fontSize: 9,
    color: "#FF0000",
    marginTop: 5,
  },
});

const NewWhitelistModal = ({ onClose, updateList, selectedItem }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [errorList, setErrorList] = useState([]);

  const {
    list: { depot, tenant, firmwareWhitelist },
  } = useSelector(DictionariesSelector);

  const mappedTenant = useMemo(
    () =>
      Object.keys(tenant).reduce((a, item) => {
        a[item] = tenant[item][0];
        return a;
      }, {}),
    [tenant]
  );

  const onSave = useCallback(
    values => {
      const { firmwares, meterType, depot, tenant, tenantOnly } = values;
      const tenantId = mappedTenant[tenant];
      const foundFirmware = firmwareWhitelist.find(item =>
        tenantOnly ? item.tenant === tenantId : item.tenant === tenantId && item.depot === depot
      );
      if (foundFirmware && !selectedItem) {
        warnMessage("Firmware already exists, please edit if needed");
        return;
      }
      const firmwaresList =
        firmwares
          ?.split("\n")
          ?.map(item => item.trim())
          ?.filter(item => item) || [];

      const duplicates = [...new Set(firmwaresList.filter((item, index) => firmwaresList.indexOf(item) !== index))];
      if (duplicates.length) {
        setErrorList(duplicates);
        return;
      }
      const newList = [...firmwareWhitelist];
      let prepareList;

      if (!selectedItem) {
        prepareList = [
          ...newList,
          {
            ...(tenantOnly ? {} : { depot }),
            tenant: tenantId,
            firmwares: firmwaresList,
            meterType,
          },
        ];
      } else {
        prepareList = newList.map(item =>
          item.tenant === tenantId && item.depot === depot ? { ...item, meterType, firmwares: firmwaresList } : item
        );
      }

      const formattedData = {
        name: DICTIONARY_TYPES.FIRMWARE_WHITELIST,
        items: prepareList,
      };
      setIsLoading(true);
      createDictionary(formattedData)
        .then(() => {
          setIsLoading(false);
          successMessage("Dictionary saved successfully");
          onClose();
          updateList();
        })
        .catch(err => {
          setIsLoading(false);
          defaultNgiErrorMethod(err);
        });
    },
    [selectedItem, mappedTenant]
  );

  const onSubmit = useCallback(() => {
    form.submit();
    closeConfirmationModal();
  }, [form]);

  const openConfirmationModal = useCallback(e => {
    e?.preventDefault();
    setIsConfirmationOpen(true);
  }, []);

  const closeConfirmationModal = useCallback(e => {
    e?.preventDefault();
    setIsConfirmationOpen(false);
  }, []);

  const handleChangeTenant = useCallback(
    value => {
      setSelectedTenant(value);
      form.setFieldValue("depot", null);
    },
    [form]
  );

  const openConfirmCloseModal = useCallback(() => setIsConfirmCloseOpen(true), []);
  const closeConfirmCloseModal = useCallback(() => setIsConfirmCloseOpen(false), []);

  const onFormChange = useCallback(() => {
    const { tenantOnly, firmwares } = form.getFieldsValue();
    const firmwaresList =
      firmwares
        ?.split("\n")
        ?.map(item => item.trim())
        ?.filter(item => item) || [];
    const duplicates = [...new Set(firmwaresList.filter((item, index) => firmwaresList.indexOf(item) !== index))];
    if (duplicates.length) {
      setErrorList(duplicates);
    } else {
      setErrorList([]);
    }
    if (tenantOnly) {
      setDisabled(true);
      form.setFieldValue("depot", null);
      return;
    }
    setDisabled(false);
  }, [form]);

  useEffect(() => {
    if (selectedItem) {
      form.setFieldsValue({
        ...selectedItem,
        tenant: selectedItem.tenant,
        firmwares: selectedItem.firmwares.join("\n"),
      });
    }
  }, []);

  return (
    <Modal
      title={
        <div className={classes.infoContainer}>
          {selectedItem ? "Edit" : "New"} Firmware Whitelist
          <span>Please fill all mandatory fields</span>
        </div>
      }
      className={classes.modal}
      open
      onCancel={openConfirmCloseModal}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <Form onFieldsChange={onFormChange} name="newFirmwareWhitelist" form={form} onFinish={onSave}>
          <Row className={classes.formItem}>
            <Col span={12}>
              <div className={classes.fieldTitle}>Tenant*</div>
              <span className={classes.subTitle}>Select between all available tenants</span>
            </Col>
            <Col span={4}>
              {!selectedItem && (
                <Form.Item valuePropName="checked" name="tenantOnly">
                  <NGICheckbox className={classes.checkbox}>Tenant only</NGICheckbox>
                </Form.Item>
              )}
            </Col>

            <Col span={8}>
              <NGIFormItem rules={[{ required: true, message: "Please select tenant" }]} name="tenant">
                <NGISelect
                  onChange={handleChangeTenant}
                  showSearch
                  disabled={selectedItem}
                  className={classes.select}
                  placeholder="Select value"
                >
                  {Object.keys(mappedTenant)?.map(item => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </NGISelect>
              </NGIFormItem>
            </Col>
          </Row>
          <Divider />
          {(selectedItem?.depot || !selectedItem) && (
            <Row className={classes.formItem}>
              <Col span={16}>
                <div className={classes.fieldTitle}>Depot*</div>
                <span className={classes.subTitle}>Select a depot you want the firmware list to be applied to.</span>
              </Col>
              <Col span={8}>
                <NGIFormItem rules={[{ required: !disabled, message: "Please select depot" }]} name="depot">
                  <NGISelect
                    disabled={disabled || selectedItem}
                    showSearch
                    className={classes.select}
                    placeholder="Select value"
                  >
                    {selectedTenant &&
                      depot[selectedTenant] &&
                      uniq(depot[selectedTenant])
                        ?.sort()
                        ?.map(item => (
                          <Option key={item} value={item}>
                            {item}
                          </Option>
                        ))}
                  </NGISelect>
                </NGIFormItem>
              </Col>
            </Row>
          )}

          <Row className={classnames(classes.formItem, classes.marginTop)}>
            <Col span={16}>
              <div className={classes.fieldTitle}>Meter Type*</div>
              <span className={classes.subTitle}>Select a meter type.</span>
            </Col>
            <Col span={8}>
              <NGIFormItem rules={[{ required: true, message: "Please select meter type" }]} name="meterType">
                <NGISelect className={classes.select} placeholder="Select value">
                  {["All", "PX", "P4"].map(item => (
                    <Option key={item} value={item}>
                      {item}
                    </Option>
                  ))}
                </NGISelect>
              </NGIFormItem>
            </Col>
          </Row>

          <div className={classes.warnContainer}>
            If the Firmware Whitelist field is left empty, the depot will allow all firmware versions and ignore the
            tenant default whitelist.
          </div>

          <Row className={classes.formItem}>
            <Col span={24}>
              <div className={classes.fieldTitle}>Firmware List</div>
              <span className={classes.subTitle}>Please enter all firmware versions one per row</span>
            </Col>
          </Row>
          <Row className={classes.formItem}>
            <Col span={24}>
              <NGIFormItem name="firmwares">
                <NGITextArea className={classes.textarea} rows={6} />
              </NGIFormItem>
            </Col>
            {!!errorList.length && (
              <div className={classes.error}>Please clear the duplicates: {errorList.join(", ")}</div>
            )}
          </Row>

          <Divider />
          <br />
          <Space>
            <CustomButton color="outlined" onClick={onClose} text="Cancel" size="small" />
            <CustomButton type="primary" htmlType="submit" onClick={openConfirmationModal} text="Save" size="small" />
          </Space>
        </Form>
      </Spin>
      {isConfirmCloseOpen && (
        <ConfirmationModal
          description="Are you sure you want to close the dialog without saving?"
          onCancel={closeConfirmCloseModal}
          onConfirm={onClose}
        />
      )}
      {isConfirmationOpen && (
        <ConfirmationModal
          description="Are you sure you want to save dictionary?"
          onCancel={closeConfirmationModal}
          onConfirm={onSubmit}
          loading={isLoading}
        />
      )}
    </Modal>
  );
};

export default NewWhitelistModal;
